import { useViewerContext } from 'domains/viewer/Viewer/ViewerContext';
import type { ViewportsConfigurations } from 'domains/viewer/ViewportsConfigurations';
import { selectorFamily } from 'recoil';
import { useImagingContext, useActiveSlice } from '../../modules/imaging/ImagingContext';
import { activeViewportState } from 'config/recoilState';
import { viewportDisplayConfigurationSelector } from '../../../ViewportsConfigurations/state';
import { windowLevelDisplaySelector } from '../../../ViewportsConfigurations/windowLevelState';
import type {
  Stack,
  ViewportConfiguration,
  ViewportDisplayConfiguration,
  WindowLevelDisplayState,
} from '../../../ViewportsConfigurations/types';
import type { RecoilState, RecoilValueReadOnly } from 'recoil';
import type { TStackProviders } from '../../../Viewer/ViewerContext';
import type { BaseImagingProvider } from '../../modules/imaging/BaseImagingProvider';

export type ViewportsDisplayConfigurations = {
  [key: string]: Nullable<ViewportDisplayConfiguration>;
};
export type ViewportsWindowLevelStates = {
  [key: string]: Nullable<WindowLevelDisplayState>;
};
export const makePayloadGetterSelector: (arg1: {
  viewportsConfigurationsSelector: RecoilState<Nullable<ViewportsConfigurations>>;
}) => RecoilValueReadOnly<{
  activeViewportId: string;
  viewportsConfigurations: Nullable<ViewportsConfigurations>;
  viewportsDisplayConfigurations: ViewportsDisplayConfigurations;
  viewportsWindowLevelStates: ViewportsWindowLevelStates;
}> = selectorFamily({
  key: 'sdk.payloadGetter',
  get:
    ({ viewportsConfigurationsSelector }) =>
    ({ get }) => {
      const viewportsConfigurations: Nullable<ViewportsConfigurations> = get(
        viewportsConfigurationsSelector
      );
      const viewportsDisplayConfigurations: ViewportsDisplayConfigurations =
        viewportsConfigurations != null
          ? Object.fromEntries(
              Object.entries(viewportsConfigurations).map(
                ([id, config]: [string, Nullable<ViewportConfiguration>]) => [
                  id,
                  get(
                    viewportDisplayConfigurationSelector({
                      stackSmid: config?.stack?.smid,
                      viewType: config?.viewType,
                      viewportId: id,
                    })
                  ),
                ]
              )
            )
          : {};

      const viewportsWindowLevelStates: ViewportsWindowLevelStates =
        viewportsConfigurations != null
          ? Object.fromEntries(
              Object.entries(viewportsConfigurations).map(
                ([id, config]: [string, Nullable<ViewportConfiguration>]) => [
                  id,
                  get(
                    windowLevelDisplaySelector({
                      seriesSmid: config?.series?.smid,
                      viewType: config?.viewType,
                      viewportId: id,
                    })
                  ),
                ]
              )
            )
          : {};

      return {
        activeViewportId: get(activeViewportState),
        viewportsConfigurations: viewportsConfigurations,
        viewportsDisplayConfigurations: viewportsDisplayConfigurations,
        viewportsWindowLevelStates: viewportsWindowLevelStates,
      };
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export function useMockableContext(): {
  stackProviders: TStackProviders;
  imagingProvider: Nullable<BaseImagingProvider<Stack>>;
  activeSlice: number;
} {
  const { stackProviders } = useViewerContext();
  const { imagingProvider } = useImagingContext();
  const activeSlice = useActiveSlice();

  return {
    stackProviders,
    imagingProvider,
    activeSlice,
  };
}
